import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { NOTIFICATION_COLORS, NOTIFICATION_UNREAD_DOT_DESKTOP } from '@/constants/residents/mobile/notificationColors';
import { strings } from '@/constants/residents/strings';
import { setSelectedBulletinId } from '@/store/slices/residents/homeSlice';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { useMarketplaceFeedbackModal } from '@/hooks/common/useMarketplaceFeedbackModal';
import { NotificationList } from '@/components/residents/notification/NotificationList';
import { NotificationDetails } from '@/components/residents/notification/NotificationDetails';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';

export const DesktopNotificationsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const selectedId = useAppSelector((s) => s.home.selectedBulletinId || null);
  const { notifications, isLoading, formatDateMMMddyyyy, isMarketplaceFeedback, isCorkBoardWarning, markAsRead, reload } = useNotifications();
  
  const feedbackModal = useMarketplaceFeedbackModal({ onSuccess: reload });

  const onPressRow = useCallback(async (b: NotificationItem) => {
    if (isMarketplaceFeedback(b)) {
      feedbackModal.openModal(b);
      return;
    }

    if (isCorkBoardWarning(b)) {
      try { await markAsRead(b); } catch {}
      const threadId = b.corkBoardNotification?.metadata?.threadId;
      if (threadId) {
        navigation.navigate('ThreadDetail', { threadId: String(threadId) });
      }
      return;
    }
    
    try {
      await markAsRead(b);
    } catch {}
    dispatch(setSelectedBulletinId(b.id));
  }, [dispatch, isMarketplaceFeedback, isCorkBoardWarning, markAsRead, feedbackModal, navigation]);

  const renderRight = useCallback((item: NotificationItem) => null, []);

  // Allow marketplace_notification (received feedback) to show in details pane, but not marketplace_feedback (which opens a modal)
  const selected = useMemo(() => (notifications || []).find(n => n.id === selectedId && !isMarketplaceFeedback(n)) || null, [notifications, selectedId, isMarketplaceFeedback]);

  return (
    <View style={styles.root}>
      <View style={styles.container}>
        <View style={styles.leftPane}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{strings.notifications.desktop.title}</Text>
          </View>
          <NotificationList
            data={notifications}
            isLoading={isLoading}
            emptyText={strings.notifications.desktop.empty}
            onPressItem={onPressRow}
            showDateSmall
            dateFor={(b) => formatDateMMMddyyyy(b.createdAt)}
            selectedId={selectedId}
            renderRight={renderRight}
            rowVariant={'screen'}
          />
        </View>
        <View style={styles.rightPane}>
          {!selected ? (
            <View style={styles.placeholder}> 
              <Text style={styles.placeholderTitle}>{strings.notifications.desktop.placeholder.title}</Text>
              <Text style={styles.placeholderSub}>{strings.notifications.desktop.placeholder.subtitle}</Text>
            </View>
          ) : (
            <NotificationDetails selected={selected} />
          )}
        </View>
      </View>
      
      {/* Marketplace Feedback Modal */}
      <LeaveFeedbackModal
        visible={feedbackModal.isVisible}
        feedbackRequest={feedbackModal.feedbackRequest}
        onClose={feedbackModal.closeModal}
        onSubmit={feedbackModal.submitFeedback}
        isSubmitting={feedbackModal.isSubmitting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  leftPane: {
    width: 440,
    borderRightWidth: 1,
    borderRightColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  rightPane: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  headerTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '800',
    fontSize: 18,
  },
  listContent: {
    paddingVertical: 8,
  },
  itemSeparator: { height: 8 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 18,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  rowPress: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  rowSelected: {
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_PRIMARY,
  },
  rowLeft: {
    paddingRight: 8,
  },
  rowAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadBubble: {
    position: 'absolute',
    top: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetTop,
    left: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetLeft,
    width: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
    height: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
    borderRadius: NOTIFICATION_UNREAD_DOT_DESKTOP.borderRadius,
    backgroundColor: NOTIFICATION_COLORS.unreadDot,
    borderWidth: 1,
    borderColor: NOTIFICATION_COLORS.unreadDot,
  },
  rowTextWrap: {
    flex: 1,
    paddingRight: 8,
  },
  rowTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
    fontSize: 16,
  },
  rowSub: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginTop: 2,
  },
  rowDateSmall: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 10,
    marginBottom: 4,
  },
  rowRight: {
    width: 64,
    alignItems: 'flex-end',
  },
  rowDate: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 12,
  },
  loader: {
    marginTop: 24,
  },
  emptyText: {
    textAlign: 'center',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    marginTop: 48,
  },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  placeholderTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '800' },
  placeholderSub: { color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY },
  detailContent: { padding: 20, gap: 10 },
  detailTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '800', fontSize: 18 },
  detailBody: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 14 },
  trashBtn: { paddingHorizontal: 8, paddingVertical: 6 },
});

export default DesktopNotificationsScreen;


